import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Date as DateIcon } from "@mebi/ui";
import { BaseInput, type BaseInputProps } from "./BaseInput";
import { Calendar } from "./Calendar";
import { cn } from "@/lib/utils";

/**
 * DateInput Component - Figma Design Implementation with MEBI Design System
 *
 * Refactored to reduce Cognitive Complexity and improve Accessibility
 */

export interface DateInputProps extends Pick<
  BaseInputProps,
  | "name"
  | "id"
  | "label"
  | "placeholder"
  | "error"
  | "disabled"
  | "className"
  | "containerClassName"
  | "labelClassName"
  | "required"
> {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  variant?: "default" | "monthYear";
}

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_YEAR_REGEX = /^(\d{4})-(\d{2})$/;

const parseDateFromValue = (
  value: string,
  variant: "default" | "monthYear",
): Date | null => {
  if (!value) return null;

  if (variant === "monthYear") {
    const match = MONTH_YEAR_REGEX.exec(value);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      if (month >= 1 && month <= 12) {
        return new Date(year, month - 1, 1);
      }
    }
  }

  const dateOnlyMatch = DATE_ONLY_REGEX.exec(value);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Helper hook to manage DateInput logic and reduce complexity
const useDateInput = (
  initialValue: string | undefined,
  name: string | undefined,
  variant: "default" | "monthYear",
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void,
) => {
  const formatDateValueForOutput = useCallback(
    (date: Date): string => {
      if (variant === "monthYear") {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        return `${date.getFullYear()}-${month}`;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    [variant],
  );

  const [showModal, setShowModal] = useState(false);
  const [dateValue, setDateValue] = useState(initialValue || "");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setDateValue(initialValue || "");
  }, [initialValue]);

  const createSyntheticEvent = useCallback(
    (nextValue: string): React.ChangeEvent<HTMLInputElement> =>
      ({
        target: { value: nextValue, name: name },
        currentTarget: { value: nextValue, name: name },
      }) as unknown as React.ChangeEvent<HTMLInputElement>,
    [name],
  );

  const createSyntheticBlurEvent = useCallback(
    (nextValue: string): React.FocusEvent<HTMLInputElement> =>
      ({
        target: { name: name, value: nextValue },
        currentTarget: { name: name, value: nextValue },
        type: "blur",
      }) as unknown as React.FocusEvent<HTMLInputElement>,
    [name],
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      const nextValue = formatDateValueForOutput(date);
      setDateValue(nextValue);
      onChange?.(createSyntheticEvent(nextValue));
      onBlur?.(createSyntheticBlurEvent(nextValue));
      setShowModal(false);
    },
    [
      createSyntheticBlurEvent,
      createSyntheticEvent,
      formatDateValueForOutput,
      onBlur,
      onChange,
    ],
  );

  const updateMonthYear = useCallback(
    (direction: "prev" | "next") => {
      let newMonth = direction === "prev" ? currentMonth - 1 : currentMonth + 1;
      let newYear = currentYear;

      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      setCurrentMonth(newMonth);
      setCurrentYear(newYear);
    },
    [currentMonth, currentYear],
  );

  const handleMonthNavigate = useCallback(
    (direction: "prev" | "next") => {
      updateMonthYear(direction);
    },
    [updateMonthYear],
  );

  const handleOpen = useCallback(
    (disabled?: boolean) => {
      if (disabled) return;

      const now = new Date();
      let baseDate = now;
      if (dateValue) {
        const parsed = parseDateFromValue(dateValue, variant);
        if (parsed) {
          baseDate = parsed;
        }
      }
      setCurrentMonth(baseDate.getMonth());
      setCurrentYear(baseDate.getFullYear());
      setShowModal(true);
    },
    [dateValue, variant],
  );

  const formatDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) return "";
      const d = parseDateFromValue(dateStr, variant);
      if (!d) return "";

      if (variant === "monthYear") {
        return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
      }
      return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
    },
    [variant],
  );

  const selectedDate = useMemo(
    () => (dateValue ? parseDateFromValue(dateValue, variant) : null),
    [dateValue, variant],
  );

  return {
    dateValue,
    selectedDate,
    showModal,
    currentMonth,
    currentYear,
    setShowModal,
    handleDateSelect,
    handleMonthNavigate,
    handleOpen,
    formatDate,
  };
};

const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, ref) => {
  const {
    name,
    label,
    placeholder,
    error,
    disabled,
    className,
    containerClassName,
    labelClassName,
    required,
    value: propValue,
    onChange: propOnChange,
    onBlur: propOnBlur,
    variant = "default",
  } = props;

  const {
    dateValue,
    selectedDate,
    showModal,
    currentMonth,
    currentYear,
    setShowModal,
    handleDateSelect,
    handleMonthNavigate,
    handleOpen,
    formatDate,
  } = useDateInput(propValue, name, variant, propOnChange, propOnBlur);

  return (
    <>
      <BaseInput
        ref={ref}
        name={name}
        label={label}
        value={formatDate(dateValue)}
        onChange={propOnChange}
        readOnly
        onClick={() => handleOpen(disabled)}
        placeholder={
          placeholder || (variant === "monthYear" ? "MM/YYYY" : "DD/MM/YYYY")
        }
        error={error}
        disabled={disabled}
        className={cn("cursor-pointer", className)}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
        required={required}
        rightIcon={
          <button
            type="button"
            onClick={() => handleOpen(disabled)}
            className="focus:outline-none"
          >
            <DateIcon className="w-5 h-5 text-neutral-600 cursor-pointer" />
          </button>
        }
      />

      {showModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3">
          <button
            type="button"
            className="absolute inset-0 bg-black/20 backdrop-blur-sm border-none cursor-default"
            onClick={() => {
              setShowModal(false);
            }}
            aria-label="Close modal"
          />

          <div
            className={cn(
              "relative flex w-auto flex-col gap-3 rounded-3xl border border-Color-Stroke-neutral bg-Color-Background-neutral-default_primary p-3 shadow-[0_14px_34px_-18px_rgba(0,0,0,0.45)]",
            )}
          >
            <div className="overflow-hidden rounded-2xl border border-Color-Stroke-neutral bg-Color-Background-neutral-default_secondary p-2">
              <Calendar
                selectedDate={selectedDate}
                currentMonth={currentMonth}
                currentYear={currentYear}
                onDateSelect={handleDateSelect}
                onNavigate={handleMonthNavigate}
                headerClickable={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
});

DateInput.displayName = "DateInput";

export { DateInput };
